import { ErrorReportingUtils } from '@mm/plugins';
import { ObjectUtils } from '@mm/utils';
import { isEmpty } from 'lodash';
import { Dispatch } from 'redux';
import LiveChatController, {
  DeleteConversations,
  GetFilteredConversationsParams,
  GetPaginatedLCConversationSessionMessages,
  GetPaginatedLCMessages,
  UpdateConversationStatus,
  UpdateSelectedLCRemoteUserId,
} from '../controllers/LiveChatController';
import commonHandler from '../errorHandlers/common-handler';
import liveChatHandler from '../errorHandlers/live-chat-handler';
import { FilterItemOperator } from '../model/AudiencesModel';
import { LiveChatChannel } from '../model/LivechatConversationModel';
import { LivechatConversationState } from '../model/LivechatConversationStateModel';
import {
  getLivechatSavedFilterState,
  LivechatSavedFilterStateModel,
  LivechatSavedFilterStatePerPageDefault,
  LivechatSavedFilterStatePerPageModel,
} from '../model/LivechatSavedFilterStateModel';
import { selectAudienceVariables } from '../selectors/audienceSelector';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
  activeCompanyUsers,
} from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import audiencesCRUDActions from './AudiencesCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import usersCRUDActions from './UsersCRUDActions';

export type ContactsSearchResult = {
  remoteUserId: string;
  firstName: string;
  lastName: string;
  username: string;

  profilePic: string | null;
};

export type LCAttributeFilter = {
  variableId: number;
  variableType: 'SystemVariable' | 'Variable';
  operator: FilterItemOperator;
  value: string | string[];
};

export type LCConversationSubset = 'main' | 'follow_up' | 'filtered';

export type LCView = {
  conversationSubset: LCConversationSubset;
  conversationState: LivechatConversationState;
  selectedAssignee: number | 'unassigned' | 'all';
  selectedChannels: LiveChatChannel[];
  selectedTags: string[];
  selectedAttribute: LCAttributeFilter | null;
};

export type ConversationsSearchResultMatch = {
  // TODO: It can potentially be null due to Rails BE issues.
  // Come up with a good approach to handle this. Probably together with
  // https://mobilemonkey.atlassian.net/browse/MM-2696
  conversationSessionId: number;
  time: number;
  text: string;
};

export type ConversationsSearchResultResultsByUser = {
  remoteUserId: string;
  contactFullName: string;
  username: string;
  matches: ConversationsSearchResultMatch[];
  profilePic: string | null;
};

export type ConversationsSearchResult = {
  matchesTotal: number;
  resultsByUser: ConversationsSearchResultResultsByUser[];
};

export const UpdateConversationStatusAction =
  'LIVECHAT__UPDATE_CONVERSATION_STATUS_BOT_ACTION';

class LiveChatCRUDActions extends CrudActionContainer {
  constructor() {
    super('LIVECHAT');
  }

  static commonParams(state: StoreState) {
    return {
      userId: state.session.session.id,
      userAuthToken: state.session.session.authToken,
      remoteFbPageId:
        (state.connectedPage.pageDetails &&
          state.connectedPage.pageDetails.remoteId) ||
        '',
    };
  }

  sendMessage(
    remotePageId: string,
    remoteUserId: string,
    message: string,
    localPersonaId?: number,
    pageId?: number,
    likeHeart?: boolean,
    clientMessageId?: string,
  ) {
    return async () => {
      const result = await LiveChatController.sendMessage(
        remotePageId,
        remoteUserId,
        message,
        localPersonaId,
        pageId,
        likeHeart,
        clientMessageId,
      );
      return result;
    };
  }

  sendAsset(
    remotePageId: string,
    remoteUserId: string,
    assetId: number,
    localPersonaId?: number,
    clientMessageId?: string,
  ) {
    return async () => {
      const result = await LiveChatController.sendAsset(
        remotePageId,
        remoteUserId,
        assetId,
        localPersonaId,
        clientMessageId,
      );

      return result;
    };
  }

  getFollowUpConversations(params: GetFilteredConversationsParams) {
    return async () => {
      try {
        const response = await LiveChatController.getFilteredConversations(
          params,
        );
        return response;
      } catch (e) {
        // Don't explode if we fail to refresh.
        ErrorReportingUtils.captureException(e);
      }
    };
  }

  getMoreConversations(fetchParameters: GetFilteredConversationsParams) {
    return async () => {
      try {
        const response = await LiveChatController.getFilteredConversations(
          fetchParameters,
        );
        if (response && response.conversations) {
          return response.conversations;
        }
      } catch (e) {
        // Don't explode if we fail to refresh.
        ErrorReportingUtils.captureException(e);
      }
    };
  }

  redownloadSpecificConversations(
    remoteUserIds: string[],
    conversationSubset: LCConversationSubset,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const remotePageId = getState().connectedPage.pageDetails?.remoteId;

        if (!remotePageId) {
          return;
        }

        const filterParameters = {
          conversationSubset,
          conversationState: 'all' as const,
          remoteUserIds,
          remotePageId,
          page: 0,
          perPage: remoteUserIds.length,
        };
        const response = await LiveChatController.getFilteredConversations(
          filterParameters,
        );

        if (response.conversations) {
          return response.conversations;
        }
      } catch (e) {
        // Don't explode if we fail to refresh.
        ErrorReportingUtils.captureException(e);
      }
    };
  }

  getFilteredConversations(filterParameters: GetFilteredConversationsParams) {
    return async () => {
      try {
        const response = await LiveChatController.getFilteredConversations(
          filterParameters,
        );

        if (response.conversations) {
          return response.conversations;
        }
      } catch (e) {
        // Don't explode if we fail to refresh.
        ErrorReportingUtils.captureException(e);
      }
    };
  }

  getFilteredContactsCount(filterParameters: GetFilteredConversationsParams) {
    return async () => {
      try {
        const response = await LiveChatController.getFilteredContactsCount(
          filterParameters,
        );

        return response.contactsCount;
      } catch (e) {
        // Don't explode if we fail to refresh.
        ErrorReportingUtils.captureException(e);
      }
    };
  }

  getConversationSession(params: GetPaginatedLCConversationSessionMessages) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const state = getState();
        const res = await LiveChatController.getConversationSessionMessages({
          ...params,
          ...LiveChatCRUDActions.commonParams(state),
        });
        return res;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getPaginatedMessages(params: GetPaginatedLCMessages) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const state = getState();
        const res = await LiveChatController.getPaginatedMessages({
          ...params,
          ...LiveChatCRUDActions.commonParams(state),
        });
        return res;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  batchGetPaginatedMessages(params: GetPaginatedLCMessages[]) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const state = getState();
        const res = await LiveChatController.batchGetPaginatedMessages({
          ...LiveChatCRUDActions.commonParams(state),
          conversations: params,
        });
        return res;
      } catch (e) {
        // Note: the error is quite common when doing reloads and it sends too many error memssages to Sentry
        // while not really bing helpful
        // commonHandler(dispatch)(e);
        return;
      }
    };
  }

  updateSelectedRemoteUserId(params: UpdateSelectedLCRemoteUserId) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const state = getState();

        const res = await LiveChatController.updateSelectedRemoteUserId({
          ...params,
          ...LiveChatCRUDActions.commonParams(state),
        });
        return res;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  updateConversationStatus(params: UpdateConversationStatus) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const result = await LiveChatController.updateConversationStatus({
          ...params,
        });

        return result;
      } catch (e) {
        liveChatHandler(dispatch)(e, UpdateConversationStatusAction);
      }
    };
  }

  updateSingleConversationStatus(params: UpdateConversationStatus) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const result = await LiveChatController.updateSingleConversationStatus({
          ...params,
        });

        return result;
      } catch (e) {
        liveChatHandler(dispatch)(e, UpdateConversationStatusAction);
      }
    };
  }

  deleteConversations(params: DeleteConversations) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const result = await LiveChatController.deleteConversations(params);

        return result;
      } catch (e) {
        liveChatHandler(dispatch)(e, UpdateConversationStatusAction);
      }
    };
  }

  searchContacts(language: string, query: string) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const state = getState();
        const res = await LiveChatController.searchContacts({
          language,
          query,
          ...LiveChatCRUDActions.commonParams(state),
        });
        if (res) {
          const resJson = await res.json();
          return ObjectUtils.deepCamelizeKeys(
            resJson,
          ) as ContactsSearchResult[];
        }
        return undefined;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  searchConversations(language: string, query: string) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const state = getState();
        const res = await LiveChatController.searchConversations({
          language,
          query,
          ...LiveChatCRUDActions.commonParams(state),
        });
        if (res) {
          const resJson = await res.json();
          return ObjectUtils.deepCamelizeKeys(
            resJson,
          ) as ConversationsSearchResult;
        }
        return undefined;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getSavedView() {
    return async (
      _dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const { userAttributes } = state.session.session;
      const remoteFbPageId = state.connectedPage.pageDetails?.remoteId;

      const companyUsers = activeCompanyUsers(state);
      const variables = selectAudienceVariables(state);

      return getLivechatSavedFilterState(
        userAttributes.lcFilterSetting,
        remoteFbPageId,
        companyUsers,
        variables,
      );
    };
  }

  storeViewAttributes(viewUpdate: Partial<LCView>) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const userId = state.session.session.id;
      const { userAttributes } = state.session.session;
      const remoteFbPageId = state.connectedPage.pageDetails?.remoteId;

      if (!remoteFbPageId || !userAttributes.activeCompanyId) {
        return;
      }

      const savedValue = await dispatch(this.getSavedView());
      const pageValue =
        savedValue[remoteFbPageId] || LivechatSavedFilterStatePerPageDefault;

      const fieldsToSave: [
        keyof LCView,
        keyof LivechatSavedFilterStatePerPageModel,
      ][] = [
        ['selectedAssignee', 'assigneeFilterSetting'],
        ['conversationState', 'conversationFilterState'],
        ['selectedChannels', 'selectedChannels'],
        ['selectedTags', 'selectedTags'],
        ['selectedAttribute', 'selectedAttribute'],
      ];
      const savedValueUpdate: Partial<LivechatSavedFilterStatePerPageModel> =
        {};
      fieldsToSave.forEach(fields => {
        const [viewUpdateField, pageValueField] = fields;
        if (
          viewUpdate[viewUpdateField] !== undefined &&
          pageValue[pageValueField] !== viewUpdate[viewUpdateField]
        ) {
          savedValueUpdate[pageValueField] = viewUpdate[viewUpdateField] as any;
        }
      });

      if (!isEmpty(savedValueUpdate)) {
        const newValue: LivechatSavedFilterStateModel = {
          ...savedValue,
          [remoteFbPageId]: {
            ...(savedValue[remoteFbPageId] ||
              LivechatSavedFilterStatePerPageDefault),
            ...savedValueUpdate,
          },
        };

        dispatch(
          usersCRUDActions.updateUserAttributes(
            userAttributes.activeCompanyId,
            userId,
            {
              lcFilterSetting: JSON.stringify(newValue),
            },
            true,
          ),
        );
      }
    };
  }

  getStoredView() {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const chatbotId = activeChatbotIdSelector(state);
      const botId = activeChatbotSelector(state).id;
      const remoteFbPageId = state.connectedPage.pageDetails?.remoteId;

      if (!remoteFbPageId || botId <= 0 || chatbotId <= 0) {
        return;
      }
      if (!selectAudienceVariables(state).length) {
        await dispatch(
          audiencesCRUDActions.getAudienceVariable(botId || chatbotId),
        );
      }

      const savedValue = await dispatch(this.getSavedView());
      const pageValue =
        savedValue[remoteFbPageId] || LivechatSavedFilterStatePerPageDefault;

      const viewUpdate = {
        conversationState: pageValue.conversationFilterState || 'main',
        selectedAssignee: pageValue.assigneeFilterSetting || 'all',
        selectedChannels: pageValue.selectedChannels || [],
        selectedTags: pageValue.selectedTags || [],
        selectedAttribute: pageValue.selectedAttribute || null,
      };

      return viewUpdate;
    };
  }
}

const liveChatCRUDActions = new LiveChatCRUDActions();

export default liveChatCRUDActions;

export const useLiveChatCRUDActions = getActionHookBuilder(
  liveChatCRUDActions,
  [
    'getFollowUpConversations',
    'getMoreConversations',
    'getPaginatedMessages',
    'batchGetPaginatedMessages',
    'getConversationSession',
    'getStoredView',
    'searchContacts',
    'searchConversations',
    'storeViewAttributes',
    'updateConversationStatus',
    'updateSingleConversationStatus',
    'updateSelectedRemoteUserId',
    'deleteConversations',
    'redownloadSpecificConversations',
    'getFilteredConversations',
    'getFilteredContactsCount',
  ],
);
